/* ===================================================================
   Integrations layer hooks (Hopper, 2026-06-17).

   - useIntegrationKey()   GET  /api/v1/integrations/key      (webhook URL + masked keys)
   - useCreateIntegrationKey()  POST same                      (mint/rotate; plaintext once)
   - useConnectors()       GET  /api/v1/integrations/connectors (honest status)
   - useDisconnectConnector()   DELETE                          (drop tokens)
   - useImportExistingReviews() POST /api/v1/integrations/import-reviews

   All read hooks are disabled until a Bearer rr_ token exists. Mutations
   invalidate the relevant queries so the UI reflects real state.
   =================================================================== */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';
import {
  api,
  type BaselineImportResult,
  type ConnectKeyResult,
  type ConnectorsResponse,
  type ConnectWebhookResult,
  type DisconnectGoogleResult,
  type FindPlaceResult,
  type IntegrationKeyCreated,
  type IntegrationKeyResponse,
  type SaveWebhookSecretResult,
} from '../lib/api';
import { useApiToken } from './useApiToken';
import { qk } from './queryKeys';

export function useIntegrationKey(): UseQueryResult<IntegrationKeyResponse, Error> {
  const hasToken = Boolean(useApiToken());
  return useQuery({
    queryKey: qk.integrationKey(),
    queryFn: ({ signal }) => api.getIntegrationKey(signal),
    enabled: hasToken,
    staleTime: 60_000,
  });
}

export interface UseCreateIntegrationKeyResult {
  create: (rotate?: boolean) => Promise<IntegrationKeyCreated>;
  isPending: boolean;
  error: Error | null;
}

export function useCreateIntegrationKey(): UseCreateIntegrationKeyResult {
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: (rotate?: boolean) => api.createIntegrationKey(rotate ?? true),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.integrationKey() });
    },
  });
  return {
    create: (rotate) => m.mutateAsync(rotate),
    isPending: m.isPending,
    error: m.error ?? null,
  };
}

export function useConnectors(): UseQueryResult<ConnectorsResponse, Error> {
  const hasToken = Boolean(useApiToken());
  return useQuery({
    queryKey: qk.connectors(),
    queryFn: ({ signal }) => api.listConnectors(signal),
    enabled: hasToken,
    staleTime: 30_000,
  });
}

export interface UseDisconnectConnectorResult {
  disconnect: (provider: string) => Promise<unknown>;
  isPending: boolean;
  error: Error | null;
}

export function useDisconnectConnector(): UseDisconnectConnectorResult {
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: (provider: string) => api.disconnectConnector(provider),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.connectors() });
    },
  });
  return {
    disconnect: (provider) => m.mutateAsync(provider),
    isPending: m.isPending,
    error: m.error ?? null,
  };
}

/* Key-based connector connect (HouseCall Pro): paste API key, then save the
   per-account webhook signing secret. Both invalidate the connectors query so
   the card reflects real state (connected -> active). */
/* Google listing DISCONNECT (the "change my listing" way out). Clears the
   connection server-side (pinning columns + gbp_location_id), then invalidates
   every read that reflects Google connectedness so the Google card drops back
   to its unconfirmed "Find my business" state and the owner can re-pick the
   correct listing. The business profile is left intact (Settings still edits
   it). Mirrors ConfirmBusinessCard's inline api.disconnect() call, wrapped as a
   TanStack mutation so the Integrations page shares the invalidation plumbing. */
export interface UseDisconnectGoogleResult {
  disconnect: () => Promise<DisconnectGoogleResult>;
  isPending: boolean;
  error: Error | null;
}

export function useDisconnectGoogle(): UseDisconnectGoogleResult {
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: () => api.disconnect(),
    onSuccess: () => {
      // /me drives the Google card's status/listingConfirmed props; reviews +
      // dashboard reflect the (now detached) listing's imported reviews.
      void qc.invalidateQueries({ queryKey: qk.me() });
      void qc.invalidateQueries({ queryKey: qk.reviews() });
      void qc.invalidateQueries({ queryKey: qk.dashboardSummary() });
    },
  });
  return {
    disconnect: () => m.mutateAsync(),
    isPending: m.isPending,
    error: m.error ?? null,
  };
}

export interface UseConnectConnectorKeyResult {
  connectKey: (provider: string, apiKey: string) => Promise<ConnectKeyResult>;
  isPending: boolean;
  error: Error | null;
}

export function useConnectConnectorKey(): UseConnectConnectorKeyResult {
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: ({ provider, apiKey }: { provider: string; apiKey: string }) =>
      api.connectConnectorKey(provider, apiKey),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.connectors() });
    },
  });
  return {
    connectKey: (provider, apiKey) => m.mutateAsync({ provider, apiKey }),
    isPending: m.isPending,
    error: m.error ?? null,
  };
}

export interface UseSaveWebhookSecretResult {
  saveSecret: (provider: string, webhookSecret: string) => Promise<SaveWebhookSecretResult>;
  isPending: boolean;
  error: Error | null;
}

export function useSaveConnectorWebhookSecret(): UseSaveWebhookSecretResult {
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: ({ provider, webhookSecret }: { provider: string; webhookSecret: string }) =>
      api.saveConnectorWebhookSecret(provider, webhookSecret),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.connectors() });
    },
  });
  return {
    saveSecret: (provider, webhookSecret) => m.mutateAsync({ provider, webhookSecret }),
    isPending: m.isPending,
    error: m.error ?? null,
  };
}

/* Guided WEBHOOK connector connect (Workiz / Sweep&Go / ResponsiBid). Creates
   the connector row server-side + returns the per-tenant webhook URL and steps.
   Invalidates the connectors query so the card flips to its connected state. */
export interface UseConnectConnectorWebhookResult {
  connectWebhook: (provider: string) => Promise<ConnectWebhookResult>;
  isPending: boolean;
  error: Error | null;
}

export function useConnectConnectorWebhook(): UseConnectConnectorWebhookResult {
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: (provider: string) => api.connectConnectorWebhook(provider),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.connectors() });
    },
  });
  return {
    connectWebhook: (provider) => m.mutateAsync(provider),
    isPending: m.isPending,
    error: m.error ?? null,
  };
}

export interface UseImportExistingReviewsResult {
  importReviews: (platforms?: string[]) => Promise<BaselineImportResult>;
  isPending: boolean;
  error: Error | null;
}

export function useImportExistingReviews(): UseImportExistingReviewsResult {
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: (platforms?: string[]) => api.importExistingReviews(platforms),
    onSuccess: () => {
      // True totals changed -> refresh anything that reads the reviews table.
      void qc.invalidateQueries({ queryKey: qk.reviews() });
      void qc.invalidateQueries({ queryKey: qk.reports() });
      void qc.invalidateQueries({ queryKey: qk.dashboardSummary() });
      void qc.invalidateQueries({ queryKey: qk.activity() });
    },
  });
  return {
    importReviews: (platforms) => m.mutateAsync(platforms),
    isPending: m.isPending,
    error: m.error ?? null,
  };
}

export interface UseFindMyBusinessResult {
  findBusiness: (name: string, address?: string) => Promise<FindPlaceResult>;
  isPending: boolean;
  error: Error | null;
}

/* Google Places "find my business" fallback. On success a place_id is stamped
   on the client row (so review import has a target) -> refresh /me so the
   google_location_known flag flips true and the UI advances to "import". */
export function useFindMyBusiness(): UseFindMyBusinessResult {
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: ({ name, address }: { name: string; address?: string }) =>
      api.findMyBusiness(name, address),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.me() });
    },
  });
  return {
    findBusiness: (name, address) => m.mutateAsync({ name, address }),
    isPending: m.isPending,
    error: m.error ?? null,
  };
}
